/**
 * Per-agent evidence facts — the teaching layer merchants pay to see.
 *
 * ADR-0159 §3 introduces the grade-A / grade-B discipline: grade-A facts
 * are adversarially verified vendor-primary and render bare (e.g. "OpenAI
 * documents four bots"); grade-B facts render with a `frame` prefix
 * ("Documented by vendor:", "Observed:", "Reported:").
 *
 * Facts here are drawn from the 2026-07-04 deep-research pass:
 *   - OpenAI's four documented bots + RFC 9421 signatures on ChatGPT
 *     agent mode + JWKS well-known URL
 *   - Anthropic's three-token roster (ClaudeBot, Claude-User,
 *     Claude-SearchBot) + `claude.com/crawling/bots.json`
 *   - Perplexity's two tokens (PerplexityBot honors robots, Perplexity-
 *     User does not, per vendor docs)
 *   - Meta's four documented crawlers: `meta-externalagent` (training),
 *     `meta-externalfetcher` (user-invoked, robots-exempt), `Meta-WebIndexer`
 *     (AI-search-quality) and `Meta-ExternalAds` (ads / business). All UA-
 *     only — no signed handshake. The WebIndexer + ExternalAds pair was
 *     grade-A-confirmed 2026-07-04 against the vendor URL (was ADR-0159
 *     deferred grade-B).
 *   - Grok's observed residential-IP rotation + spoofed browser UAs
 *     (framed as reported/observed)
 *   - Microsoft Copilot Actions: unidentifiable browser UA (observed)
 *   - Claude connectors → Square Cash App (grade-B, framed)
 *   - Perplexity + PayPal Instant Buy (grade-B, framed)
 *   - Copilot Checkout via feeds (grade-B, framed)
 *   - UCP + Google Pay waitlist (grade-B, framed)
 *   - Stripe owns SPT minting/redemption (grade-A caption)
 *
 * ADR-0159 deferred grade-B items resolved 2026-07-04: Meta-WebIndexer +
 * Meta-ExternalAds were grade-A-confirmed live against the vendor URL and
 * folded into the meta claim above (companion migration
 * `20260704000040_agent_registry_meta_webindexer_externalads.sql`).
 *
 * A future doc revision touches this one file. The journey builders read
 * from `AGENT_EVIDENCE` by roster id.
 */

export interface EvidenceFact {
  /** The copy line rendered in the identity evidence panel. */
  readonly claim: string;
  /**
   * `'A'` — adversarially verified vendor-primary (renders bare).
   * `'B'` — extracted, requires a `frame` prefix on render.
   */
  readonly grade: 'A' | 'B';
  /** Vendor URL or primary source (rendered as a mono citation). */
  readonly source: string;
  /**
   * Framing prefix for grade-B claims. 'documented-by-vendor' renders
   * as "Documented by vendor:"; `'observed'` as "Observed:"; `'reported'`
   * as "Reported:". Absent on grade-A.
   */
  readonly frame?: 'documented-by-vendor' | 'observed' | 'reported';
}

/**
 * Per-agent evidence lists, keyed by roster id (see `roster.ts::AGENTS`).
 * Every roster entry MUST have an entry here — a missing entry means the
 * `IdentityEvidencePanel` renders nothing for that agent (acceptable for
 * `custom` / `unverified`, which have no vendor evidence to cite).
 */
export const AGENT_EVIDENCE: Readonly<Record<string, readonly EvidenceFact[]>> = {
  // ── OpenAI (ChatGPT) — four documented bots + RFC 9421 signatures ────
  chatgpt: [
    {
      claim:
        'OpenAI documents four bots: GPTBot (training), OAI-SearchBot (search), ChatGPT-User (user-invoked browse), OAI-AdsBot (ads).',
      grade: 'A',
      source: 'platform.openai.com/docs/bots',
    },
    {
      // Merged from three facts (2026-07-06 consolidation): header
      // mechanics + JWKS + source-IP verification collapsed into the one
      // sentence a merchant needs; the source line keeps the key URL.
      claim:
        'ChatGPT agent mode cryptographically signs its web requests (RFC 9421, Ed25519) and publishes the verifying key — the only agentic browser that does.',
      grade: 'A',
      source: 'chatgpt.com/.well-known/http-message-signatures-directory',
    },
    {
      // Merchant-legible rewrite (2026-07-05): the old copy leaked
      // internal endpoint paths, ship dates, and ADR citations into a
      // customer-facing panel — and cited our own ADRs under the
      // "documented by vendor" frame, a category error in the evidence
      // discipline. Vendor claims cite vendor sources only.
      claim:
        'Agentic Commerce Protocol checkout (OpenAI + Stripe): the agent submits the order for approval before anything is charged, and payment settles through a token issued by Stripe — neither the agent nor the store handles card details. OpenAI discontinued ChatGPT’s in-app Instant Checkout surface in March 2026; the protocol and Stripe’s tokens persist independently.',
      grade: 'B',
      source: 'OpenAI + Stripe Agentic Commerce Protocol announcement, 2025-09; OpenAI Instant Checkout wind-down, 2026-03',
      frame: 'documented-by-vendor',
    },
  ],

  // ── Anthropic (Claude) — three-token roster ─────────────────────────
  claude: [
    {
      claim:
        "Anthropic documents three bots: ClaudeBot (training crawler), Claude-User (user-invoked fetch), Claude-SearchBot (search-quality). Source-IP verification via claude.com/crawling/bots.json.",
      grade: 'A',
      source: 'claude.com/crawling/bots.json',
    },
    {
      claim:
        'Claude-in-Chrome (the experimental in-browser assistant) is unidentifiable — no distinct bot token; a stock Chrome UA.',
      grade: 'A',
      source: 'docs.claude.com/en/docs/agents/web-crawlers',
    },
    {
      claim:
        "Claude transacts through connectors — e.g. the Square 'Order by Cash App' connector, live 2026-07-01. The merchant handoff is the connector's payment rail, not a Sill signed mandate.",
      grade: 'B',
      source: 'Square connector announcement, 2026-07-01',
      frame: 'documented-by-vendor',
    },
  ],

  // ── Perplexity — robots.txt-exempt user fetch ───────────────────────
  perplexity: [
    {
      claim:
        'Perplexity documents two bots: PerplexityBot (search-index crawler, honors robots.txt) and Perplexity-User (user-invoked fetch, robots.txt "generally ignored" per vendor docs).',
      grade: 'A',
      source: 'docs.perplexity.ai/guides/bots',
    },
    {
      claim:
        'PayPal Instant Buy — Perplexity checkout runs on a documented enrolled-merchant set, not a signed-mandate rail.',
      grade: 'B',
      source: 'PayPal Instant Buy for Perplexity, vendor announcement',
      frame: 'documented-by-vendor',
    },
  ],

  // ── Google (Gemini) — A2A + AP2 + UCP/Google Pay waitlist ──────────
  gemini: [
    {
      claim:
        'Google-Extended is the documented fetch UA opt-in signal for Gemini training and agent-mode fetches.',
      grade: 'A',
      source: 'developers.google.com/search/docs/crawling-indexing/google-common-crawlers',
    },
    {
      claim:
        "AP2 (Google's Agentic Payments Protocol) mandates interop with A2A and MCP. Sill's mandate model is compatible with the AP2 IntentMandate / CartMandate / PaymentMandate shapes.",
      grade: 'A',
      source: 'ap2.dev + a2a.dev',
    },
    {
      claim:
        'UCP + Google Pay: agentic checkout runs on Google surfaces (waitlist-gated). Not a self-serve integration.',
      grade: 'B',
      source: 'Google Pay + UCP developer preview',
      frame: 'documented-by-vendor',
    },
  ],

  // ── Grok — reported residential-IP rotation + spoofed UAs ──────────
  grok: [
    {
      claim:
        'Grok has no documented bot UA and no cryptographic identity. Observed traffic uses browser-shaped UAs (Chrome/Safari) with reported residential-IP rotation.',
      grade: 'B',
      source: 'operator reports + observed traffic samples',
      frame: 'observed',
    },
    {
      claim:
        'Residential-IP rotation across ISPs — invisible without a signed handshake. This is what the merchant CANNOT see without a signature.',
      grade: 'B',
      source: 'network operator community reports, 2026',
      frame: 'reported',
    },
  ],

  // ── Microsoft (Copilot) — bingbot + unidentifiable Actions ─────────
  copilot: [
    {
      claim:
        'bingbot is the canonical Bing crawler and grounds Microsoft Copilot web answers.',
      grade: 'A',
      source: 'bing.com/webmasters/help/which-crawlers-does-bing-use',
    },
    {
      claim:
        'Copilot Actions (the browsing sub-agent) uses a stock Edge/Chromium UA with no bot token — unidentifiable without a signature.',
      grade: 'B',
      source: 'Microsoft Copilot Actions rollout, observed 2026',
      frame: 'observed',
    },
    {
      claim:
        'Copilot Checkout is fed by Microsoft product feeds / ads, not a signed-mandate rail.',
      grade: 'B',
      source: 'Microsoft Copilot commerce overview',
      frame: 'documented-by-vendor',
    },
  ],

  // ── Meta (Meta AI) — four documented UAs, no signature ─────────────
  meta: [
    {
      claim:
        'Meta documents four crawlers: meta-externalagent (Llama training), meta-externalfetcher (user-invoked fetch — WhatsApp / Instagram / Facebook, robots.txt-exempt), Meta-WebIndexer (AI-search-quality indexing), and Meta-ExternalAds (ads / business-related crawls). UA-only identity — no signed handshake.',
      grade: 'A',
      source: 'developers.facebook.com/docs/sharing/webmasters/web-crawlers',
    },
  ],

  // ── Mistral — discovery-only ────────────────────────────────────────
  mistral: [
    {
      claim:
        'MistralAI-User is Mistral\'s user-invoked fetch UA (Le Chat). Honors robots.txt per vendor docs.',
      grade: 'A',
      source: 'docs.mistral.ai/robots',
    },
  ],

  // ── Apple — Applebot-Extended (opt-out training signal) ─────────────
  apple: [
    {
      claim:
        'Applebot-Extended is Apple\'s opt-out training signal for Apple Intelligence. UA is Mozilla-wrapped; no signed handshake.',
      grade: 'A',
      source: 'support.apple.com/en-us/119829',
    },
  ],

  // ── DuckDuckGo — DuckAssistBot (user-invoked retrieval) ─────────────
  duckduckgo: [
    {
      claim:
        'DuckAssistBot fires on user queries that activate DuckAssist. Not a training crawler.',
      grade: 'A',
      source: 'duckduckgo.com/duckassistbot.html',
    },
  ],

  // ── Custom / Unverified — no vendor evidence ────────────────────────
  custom: [],
  unverified: [],
};
